import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, FileText, FolderArchive, Info, Loader2, Search } from 'lucide-react';
import { binder, studies, type StudyRow } from '@/api/client';
import { fmtDateTime } from '@/lib/dates';
import { PageHeader } from '@/components/layout/PageHeader';
import { BlindingScopeBadge } from '@/components/binder/BlindingScopeBadge';
import { binderScope, sortBindersPrimaryFirst } from '@/lib/binderScope';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';
import { studyLabel } from '@/lib/studyLabel';

/**
 * eISF Binder list.
 *
 * Auto-redirects only when there's exactly ONE binder (the common
 * single-study site). Otherwise renders a searchable list so users
 * can find the right binder among many. Search filters on:
 *  - binder reference number (BIN-####)
 *  - binder display label / study protocol number / study title
 */
export default function BinderListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const bindersQ = useQuery({ queryKey: ['binder', 'list'], queryFn: () => binder.list() });
  // Studies whose binder is still locked for the caller: on the team, but
  // their DoA row is not signed and PI-countersigned yet.
  const doaBindersQ = useQuery({
    queryKey: ['binder', 'list', 'doa'],
    queryFn: () => binder.listForDoa(),
    staleTime: 60_000,
  });
  const studiesQ = useQuery({
    queryKey: ['studies', 'for-binder-list'],
    queryFn: () => studies.list(),
    staleTime: 60_000,
  });

  const binders = useMemo(() => bindersQ.data?.binders ?? [], [bindersQ.data]);
  const studyById = useMemo(() => {
    const map = new Map<string, StudyRow>();
    for (const s of studiesQ.data?.rows ?? []) map.set(s.study_id, s);
    return map;
  }, [studiesQ.data]);

  const lockedBinders = useMemo(() => {
    const open = new Set(binders.map((b) => b.binder_id));
    return sortBindersPrimaryFirst(doaBindersQ.data?.binders ?? [])
      .filter((b) => !open.has(b.binder_id) && binderScope(b) !== 'unblinded');
  }, [binders, doaBindersQ.data]);
  const lockedNote = lockedBinders.length > 0 && (
    <div className="mx-auto w-full max-w-2xl rounded-md border border-amber-300 bg-amber-50 p-3 text-left text-sm text-amber-900">
      <p className="font-medium">Waiting for your Delegation of Authority</p>
      <p className="mt-0.5 text-xs">
        A study binder opens once its DoA version becomes active — after every staff member
        on it has signed and the PI has countersigned each row.
      </p>
      <ul className="mt-2 space-y-1">
        {lockedBinders.map((b) => {
          const study = studyById.get(b.study_id);
          return (
            <li key={b.binder_id} className="flex items-center justify-between gap-2">
              <span className="truncate">
                {study ? studyLabel(study) : b.reference_number}
              </span>
              <button
                type="button"
                className="btn-secondary shrink-0 text-xs"
                onClick={() => navigate(`/binder/${encodeURIComponent(b.reference_number ?? b.binder_id)}/doa/grid`)}
              >
                Open DoA
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );

  // Auto-redirect only when exactly one binder is visible — matches the
  // single-study-site case that motivated the original redirect. When
  // multiple binders exist, show the list so users can search.
  useEffect(() => {
    if (!bindersQ.data || binders.length !== 1) return;
    const only = binders[0];
    if (only?.reference_number) {
      navigate(`/binder/${encodeURIComponent(only.reference_number)}`, { replace: true });
    }
  }, [bindersQ.data, binders, navigate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = sortBindersPrimaryFirst(binders);
    if (!q) return sorted;
    return sorted.filter((b) => {
      const study = studyById.get(b.study_id);
      const proto = (study?.protocol_number ?? '').toLowerCase();
      const title = (study?.title ?? '').toLowerCase();
      const ref = (b.reference_number ?? '').toLowerCase();
      const name = (b.binder_name ?? '').toLowerCase();
      return (
        ref.includes(q) ||
        name.includes(q) ||
        proto.includes(q) ||
        title.includes(q)
      );
    });
  }, [binders, search, studyById]);

  if (bindersQ.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 p-16 text-ink-muted">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading binders…
      </div>
    );
  }

  if (bindersQ.isError) {
    return (
      <div className={`flex items-start gap-2 rounded border p-4 text-sm m-6 ${errorToneClass(bindersQ.error)}`}>
        {isPermissionError(bindersQ.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        <span>{errorText(bindersQ.error, 'Failed to load binders.')}</span>
      </div>
    );
  }

  if (binders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-16 text-ink-muted">
        {lockedNote || (
          <>
            <FolderArchive className="h-10 w-10 opacity-40" />
            <p className="text-sm">No binders yet. Activate a study to provision an eISF binder.</p>
          </>
        )}
      </div>
    );
  }

  if (binders.length === 1) {
    // useEffect above will redirect momentarily.
    return (
      <div className="flex items-center justify-center gap-2 p-16 text-ink-muted">
        <Loader2 className="h-5 w-5 animate-spin" /> Opening binder…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="eISF binders"
        subtitle={`${binders.length} binder${binders.length === 1 ? '' : 's'} across your studies. Pick one to open its folder tree.`}
      />
      {lockedNote}

      <div className="card p-3">
        <div className="relative max-w-md">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-muted" />
          <input
            type="search"
            placeholder="Search by BIN-#, protocol number, study title, or label…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-7 text-xs"
          />
        </div>
        <div className="mt-1 text-[10px] text-ink-muted">
          {filtered.length} of {binders.length} match
        </div>
      </div>

      <div className="card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-ink-muted">
            No binders match "{search}".
          </div>
        ) : (
          <ul className="divide-y divide-surface-border">
            {filtered.map((b) => {
              const study = studyById.get(b.study_id);
              const displayName = b.binder_name || (study ? studyLabel(study) : '(unnamed binder)');
              return (
                <li key={b.binder_id}>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/binder/${encodeURIComponent(b.reference_number ?? b.binder_id)}`)
                    }
                    className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-xs hover:bg-surface-muted/40"
                  >
                    <FileText className="h-4 w-4 shrink-0 text-ink-muted" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium text-ink-primary">{displayName}</span>
                        {b.reference_number && (
                          <span className="font-mono text-[10px] text-ink-muted">{b.reference_number}</span>
                        )}
                        <BlindingScopeBadge scope={binderScope(b)} />
                        <span
                          className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${
                            b.status === 'active'
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                              : 'border-slate-200 bg-slate-100 text-slate-600'
                          }`}
                        >
                          {b.status}
                        </span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-ink-muted">
                        Created {fmtDateTime(b.created_at)}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
